import { dev } from '$app/env';
import _ from 'lodash';
import { env } from 'node:process';
import pRetry, { type Options } from 'p-retry';

export function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getLastDate(name: string) {
	const id = 'show-notification-' + name;
	const lastDate = (() => {
		const currentValue = localStorage.getItem(id);
		if (!currentValue) {
			return new Date();
		}
		return new Date(parseInt(currentValue));
	})();
	localStorage.setItem(id, Date.now().toString());
	return lastDate;
}

export function showNotificationAgain(name: string, duration: number) {
	const id = 'show-notification-' + name;
	const currentValue = localStorage.getItem(id);
	if (!currentValue) {
		localStorage.setItem(id, Date.now().toString());
		return true;
	}
	if (parseInt(currentValue) + duration < Date.now()) {
		localStorage.setItem(id, Date.now().toString());
		return true;
	}
	return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function singleton<T extends (...args: any[]) => any>(key: string, func: T) {
	// Use Parameters<T> to infer the argument types of 'func'
	// Use ReturnType<T> to infer the return type of 'func'
	return (...args: Parameters<T>): ReturnType<T> => {
		if (window.singletons !== undefined && window.singletons[key] !== undefined) {
			return window.singletons[key] as ReturnType<T>;
		}
		const result = func(...args);
		if (window.singletons === undefined) {
			window.singletons = {} as Record<string, unknown>;
		}
		window.singletons[key] = result;
		return result;
	};
}

export function unwrap<T>(value: T | null | undefined, msg = 'Unexpected undefined'): T {
	if (value == null) {
		throw new Error(msg);
	}
	return value;
}

export const negate =
	<T>(fn: (arg: T) => Promise<boolean>) =>
	async (arg: T) =>
		!(await fn(arg));

export const retry = async <T>(
	input: (attemptNumber: number) => PromiseLike<T> | T,
	options?: Options
) => await pRetry<T>(input, { minTimeout: 200, ...options });

const fetchRetryInternal = async (
	input: string | URL | Request,
	init?: RequestInit,
	options?: Options
) => {
	if (import.meta.env.MODE == 'development') {
		const extendedInit = init ?? {};
		extendedInit.headers = {
			'x-vercel-protection-bypass': 'MN1NpHGCnnpaRILuT2U0uBB0aDXBiXJM',
			...extendedInit.headers
		};
		return await retry(async () => await fetch(input, extendedInit), options);
	} else {
		return await retry(async () => await fetch(input, init), options);
	}
};

fetchRetryInternal.json = (
	input: string | URL | Request,
	data: unknown,
	init?: RequestInit,
	options?: Options
) =>
	fetchRetryInternal(
		input,
		{
			// @wc-ignore
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
			...init
		},
		options
	);

export const fetchRetry = fetchRetryInternal;

export const C = <T>(value: T) => value;

export const makeBearer = (key: string) => C(`Bearer ${key}`);
export const jwtAlgorithm = C('EdDSA');

export const enumKey = <E extends object, T = keyof E>(enumType: E, value: T) =>
	Object.keys(enumType).find((k) => enumType[k as keyof typeof enumType] === value)!;
